import { useState, useEffect } from 'react';
import { X, Calendar, User, FileText, Building2, AlertCircle, RotateCcw, Briefcase } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function TaskModal({ task, entities, individuals, onClose, onSave }) {
  const { token } = useAuth();
  const [internalAdvisers, setInternalAdvisers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    dueDate: '',
    owner: '',
    status: 'Pending',
    type: 'Key task',
    linkedEntityId: '',
    linkedIndividualId: '',
    notes: '',
    isRecurring: false,
    recurringFrequency: 'monthly',
    serviceLine: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        dueDate: task.dueDate || '',
        owner: task.owner || '',
        status: task.status || 'Pending',
        type: task.type || 'Key task',
        linkedEntityId: task.linkedEntityId || '',
        linkedIndividualId: task.linkedIndividualId || '',
        notes: task.notes || '',
        isRecurring: task.isRecurring || false,
        recurringFrequency: task.recurringFrequency || 'monthly'
      });
    }
  }, [task]);

  // Load internal advisers for the owner picker
  useEffect(() => {
    const t = token || localStorage.getItem('qindex_token');
    if (!t) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/internal-advisers?activeOnly=true`;
    fetch(url, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.ok ? r.json() : { advisers: [] })
      .then(data => setInternalAdvisers(data?.advisers || []))
      .catch(() => {});
  }, [token]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';
    if (!formData.owner.trim()) newErrors.owner = 'Owner is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        linkedEntityId: formData.linkedEntityId || null,
        linkedIndividualId: formData.linkedIndividualId || null,
        isRecurring: formData.isRecurring,
        recurringFrequency: formData.isRecurring ? formData.recurringFrequency : null
      });
    }
  };

  const isEditing = !!task;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in" 
      data-testid="task-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="task-modal-title"
      aria-describedby="task-modal-description"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E8E4DF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#4A3B32]" />
            </div>
            <div>
              <h2 id="task-modal-title" className="font-heading text-lg text-[#2C2420]">
                {isEditing ? 'Edit Task' : 'Create New Task'}
              </h2>
              <p id="task-modal-description" className="text-sm text-[#5D524D]">
                {isEditing ? 'Update task details and settings.' : 'Add a new task to the workflow.'}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="hover:bg-[#F0EBE6]"
            data-testid="close-task-modal-btn"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              Task Title *
            </label>
            <Input
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter task title"
              className={errors.title ? 'border-red-500' : ''}
              data-testid="task-title-input"
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.title}
              </p>
            )}
          </div>

          {/* Due Date & Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Due Date *
              </label>
              <Input
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleChange('dueDate', e.target.value)}
                className={errors.dueDate ? 'border-red-500' : ''}
                data-testid="task-duedate-input"
              />
              {errors.dueDate && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.dueDate}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Type
              </label>
              <Select value={formData.type} onValueChange={(v) => handleChange('type', v)}>
                <SelectTrigger data-testid="task-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Key task">Key task</SelectItem>
                  <SelectItem value="Key date">Key date</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Owner & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Owner *
              </label>
              {(() => {
                const advNames = internalAdvisers.map(a => a.name);
                const isCustom = formData.owner && !advNames.includes(formData.owner);
                return (
                  <>
                    <Select
                      value={isCustom ? '__custom__' : (formData.owner || '')}
                      onValueChange={(v) => {
                        if (v === '__custom__') {
                          handleChange('owner', '');
                        } else {
                          handleChange('owner', v);
                        }
                      }}
                    >
                      <SelectTrigger className={errors.owner ? 'border-red-500' : ''} data-testid="task-owner-select">
                        <SelectValue placeholder="Select internal adviser" />
                      </SelectTrigger>
                      <SelectContent>
                        {internalAdvisers.length === 0 && (
                          <SelectItem value="__none__" disabled>
                            No internal advisers — add some in the Internal Adviser Hub
                          </SelectItem>
                        )}
                        {internalAdvisers.map(adv => (
                          <SelectItem key={adv.id} value={adv.name}>
                            {adv.name}{adv.title ? ` · ${adv.title}` : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="__custom__">Other (type name)…</SelectItem>
                      </SelectContent>
                    </Select>
                    {(isCustom || formData.owner === '') && (
                      <Input
                        value={formData.owner}
                        onChange={(e) => handleChange('owner', e.target.value)}
                        placeholder="Type owner name"
                        className={`mt-2 ${errors.owner ? 'border-red-500' : ''}`}
                        data-testid="task-owner-input"
                      />
                    )}
                  </>
                );
              })()}
              {errors.owner && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.owner}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Status
              </label>
              <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Linked Entity */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              <Building2 className="w-4 h-4 inline mr-1" />
              Linked Entity
            </label>
            <Select value={formData.linkedEntityId || "none"} onValueChange={(v) => handleChange('linkedEntityId', v === "none" ? "" : v)}>
              <SelectTrigger data-testid="task-entity-select">
                <SelectValue placeholder="Select an entity (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {entities?.map(entity => (
                  <SelectItem key={entity.id} value={entity.id}>
                    {entity.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Linked Individual */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Linked Individual
            </label>
            <Select value={formData.linkedIndividualId || "none"} onValueChange={(v) => handleChange('linkedIndividualId', v === "none" ? "" : v)}>
              <SelectTrigger data-testid="task-individual-select">
                <SelectValue placeholder="Select an individual (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {individuals?.map(ind => (
                  <SelectItem key={ind.id} value={ind.id}>
                    {ind.firstName} {ind.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Service Line */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              <Briefcase className="w-4 h-4 inline mr-1" />
              Service Line
            </label>
            <Select value={formData.serviceLine || "none"} onValueChange={(v) => handleChange('serviceLine', v === "none" ? "" : v)}>
              <SelectTrigger data-testid="task-serviceline-select">
                <SelectValue placeholder="Tag with a service line (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="family-office">Family Office</SelectItem>
                <SelectItem value="wealth">Wealth Management</SelectItem>
                <SelectItem value="tax">Tax</SelectItem>
                <SelectItem value="tax-advisory">Tax Advisory</SelectItem>
                <SelectItem value="philanthropy">Philanthropy</SelectItem>
                <SelectItem value="trustee-legal">Trustee / Estate / Legal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              <FileText className="w-4 h-4 inline mr-1" />
              Notes
            </label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any additional notes..."
              rows={3}
              data-testid="task-notes-input"
            />
          </div>

          {/* Recurring Task */}
          <div className="border-t border-[#E8E4DF] pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-[#4A3B32]" />
                <Label htmlFor="recurring" className="text-sm font-medium text-[#2C2420]">
                  Recurring Task
                </Label>
              </div>
              <Switch
                id="recurring"
                checked={formData.isRecurring}
                onCheckedChange={(checked) => handleChange('isRecurring', checked)}
                data-testid="task-recurring-toggle"
              />
            </div>
            
            {formData.isRecurring && (
              <div className="ml-6">
                <label className="block text-sm font-medium text-[#2C2420] mb-1">
                  Repeat Frequency
                </label>
                <Select 
                  value={formData.recurringFrequency} 
                  onValueChange={(v) => handleChange('recurringFrequency', v)}
                >
                  <SelectTrigger data-testid="task-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="fortnightly">Fortnightly (Every 2 weeks)</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly (Every 3 months)</SelectItem>
                    <SelectItem value="biannual">Bi-Annual (Every 6 months)</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#8C8078] mt-1">
                  When completed, a new task will be created with the next due date
                </p>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#E8E4DF] bg-[#F9F7F5]">
          <Button variant="outline" onClick={onClose} className="caffeine-btn-secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="caffeine-btn" data-testid="save-task-btn">
            {isEditing ? 'Save Changes' : 'Create Task'}
          </Button>
        </div>
      </div>
    </div>
  );
}
